import { Component, Inject, Input, ChangeDetectionStrategy } from '@angular/core';

import { Contact, ChannelType } from 'src/app/services/messaging/models';
import { ChannelService } from 'src/app/services/channel/channel.service';
import { NotificationService } from 'src/app/services/notification/notification.service';
import { LoggingService } from 'src/app/services/logging/logging.service';
import { MODAL_REF, ModalRef } from 'src/app/services/modal/modal-ref.token';
import { UserService } from 'src/app/services/user/user.service';

@Component({
    selector: 'app-create-channel-modal',
    templateUrl: './create-channel-modal.component.html',
    styleUrls: ['./create-channel-modal.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class CreateChannelModalComponent {
  @Input() organizationId: string;
  @Input() contacts: Contact[] = [];

  public channelName = '';
  public description = '';
  public channelType: ChannelType = 'public';
  public selectedMemberIds: string[] = [];
  public loading = false;
  public nameError = '';

  constructor(
    public user: UserService,
    private channelService: ChannelService,
    private logging: LoggingService,
    @Inject(MODAL_REF) private modalRef: ModalRef,
    private notification: NotificationService,
  ) {
    // Auto-select the creator — they are always a member of private channels
    if (this.user.user?.uid) {
      this.selectedMemberIds = [this.user.user.uid];
    }
  }

  public toggleChannelType(): void {
    this.channelType = this.channelType === 'public' ? 'private' : 'public';
  }

  public toggleMember(contactId: string): void {
    // Creator cannot be deselected
    if (contactId === this.user.user?.uid) return;
    const index = this.selectedMemberIds.indexOf(contactId);
    if (index > -1) {
      this.selectedMemberIds.splice(index, 1);
    } else {
      this.selectedMemberIds.push(contactId);
    }
  }

  public isMemberSelected(contactId: string): boolean {
    return this.selectedMemberIds.includes(contactId);
  }

  public async createChannel(): Promise<void> {
    const name = this.channelName.trim();
    if (!name) {
      this.nameError = 'messaging.channels.name-required';
      return;
    }

    if (!/^[a-z0-9-]+$/.test(name)) {
      this.nameError = 'messaging.channels.name-invalid';
      return;
    }

    this.nameError = '';
    this.loading = true;

    try {
      const conversationId = await this.channelService.createChannel(
        this.organizationId,
        name,
        this.channelType,
        this.description.trim(),
        this.channelType === 'private' ? this.selectedMemberIds : undefined,
      );

      this.notification.display('messaging.channels.created');
      this.modalRef.dismiss({ conversationId });
    } catch (error) {
      this.logging.error('CreateChannelModal: Error creating channel', error);
      if (error?.message?.includes('already-exists')) {
        this.nameError = 'messaging.channels.name-exists';
      } else {
        this.notification.display('messaging.channels.create-error', 'danger');
      }
    } finally {
      this.loading = false;
    }
  }

  public dismiss(): void {
    this.modalRef.dismiss();
  }
}
